import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Inbox as InboxIcon, Mail, Send, Search, Filter, RefreshCw,
  MailOpen, Reply, Trash2, Archive, Star, Clock, User, Building,
  Tag, ChevronRight, MoreVertical, X, Check, AlertCircle,
  MessageSquare, ArrowLeft, Paperclip, Bold, Italic, Link,
  MousePointer, CheckCircle2, XCircle, Phone, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnifiedInbox() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationLead, setConversationLead] = useState(null);
  const [conversationEvents, setConversationEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, open: 0, replied: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    is_read: '',
    lead_status: '',
    tag: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    fetchStats();
    fetchConversations();
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [filters]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/inbox/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.is_read !== '') params.append('is_read', filters.is_read);
      if (filters.lead_status) params.append('lead_status', filters.lead_status);
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.search) params.append('search', filters.search);

      const res = await fetch(`${API_URL}/api/inbox/conversations?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setLoadingConversation(true);
    setReplyContent('');
    setConversationEvents([]);

    try {
      // Fetch conversation details and events in parallel
      const [conversationRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/inbox/conversations/${conversation.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/webhooks/resend/conversation-events/${conversation.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (conversationRes.ok) {
        const data = await conversationRes.json();
        setConversationMessages(data.messages || []);
        setConversationLead(data.lead);
        // Update local state
        setConversations(prev => prev.map(c => 
          c.id === conversation.id ? { ...c, is_read: true } : c
        ));
        // Update stats
        if (!conversation.is_read) {
          setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
        }
      }

      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setConversationEvents(eventsData.events || []);
      }
    } catch (error) {
      toast.error('Failed to load conversation');
    } finally {
      setLoadingConversation(false);
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim() || !selectedConversation) return;

    setSendingReply(true);
    try {
      const res = await fetch(`${API_URL}/api/inbox/conversations/${selectedConversation.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: replyContent })
      });

      if (res.ok) {
        toast.success('Reply sent!');
        setReplyContent('');
        // Refresh conversation
        selectConversation(selectedConversation);
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const markAsUnread = async (conversationId) => {
    try {
      await fetch(`${API_URL}/api/inbox/conversations/${conversationId}/unread`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, is_read: false } : c
      ));
      setStats(prev => ({ ...prev, unread: prev.unread + 1 }));
    } catch (error) {
      toast.error('Failed to mark as unread');
    }
  };

  const updateStatus = async (conversationId, status) => {
    try {
      await fetch(`${API_URL}/api/inbox/conversations/${conversationId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, status } : c
      ));
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(prev => ({ ...prev, status }));
      }
      fetchStats();
      toast.success(`Marked as ${status}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Delete this conversation?')) return;

    try {
      await fetch(`${API_URL}/api/inbox/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setConversationMessages([]);
      }
      fetchStats();
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'replied': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'closed': return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
      default: return 'bg-slate-500/20 text-emerald-300/70 border-slate-500/30';
    }
  };

  const getLeadStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500/20 text-blue-400',
      contacted: 'bg-yellow-500/20 text-yellow-400',
      qualified: 'bg-emerald-500/20 text-emerald-400',
      proposal: 'bg-emerald-500/20 text-emerald-400',
      won: 'bg-green-500/20 text-green-400',
      lost: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-slate-500/20 text-emerald-300/70';
  };

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-emerald-800/30">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <InboxIcon className="w-6 h-6 text-emerald-400" />
              Unified Inbox
            </h1>
            <p className="text-emerald-300/70 text-sm mt-1">Manage all your email conversations</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Stats */}
            <div className="flex items-center gap-4 mr-4">
              <div className="text-center">
                <p className="text-xl font-bold text-white">{stats.unread}</p>
                <p className="text-xs text-emerald-300/70">Unread</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-emerald-400">{stats.replied}</p>
                <p className="text-xs text-emerald-300/70">Replied</p>
              </div>
            </div>
            <Button
              onClick={() => { fetchConversations(); fetchStats(); }}
              variant="outline"
              size="sm"
              className="border-emerald-800/30 text-slate-300"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => setShowCompose(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="compose-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Compose
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversation List */}
        <div className="w-96 border-r border-emerald-800/30 flex flex-col">
          {/* Search & Filters */}
          <div className="p-3 border-b border-emerald-800/30/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-10 py-2 bg-[#0a3d2e]/50 border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                data-testid="inbox-search"
              />
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded ${showFilters ? 'text-emerald-400' : 'text-gray-500'}`}
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>

            {/* Filter Pills */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-3 space-y-2 overflow-hidden"
                >
                  <div className="flex flex-wrap gap-2">
                    {['all', 'open', 'replied', 'closed'].map(status => (
                      <button
                        key={status}
                        onClick={() => setFilters(prev => ({ ...prev, status: status === 'all' ? '' : status }))}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                          (status === 'all' && !filters.status) || filters.status === status
                            ? 'bg-emerald-600 text-white'
                            : 'bg-[#0a3d2e] text-emerald-300/70 hover:bg-slate-700'
                        }`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, is_read: prev.is_read === 'false' ? '' : 'false' }))}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        filters.is_read === 'false'
                          ? 'bg-blue-600 text-white'
                          : 'bg-[#0a3d2e] text-emerald-300/70 hover:bg-slate-700'
                      }`}
                    >
                      Unread Only
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-emerald-500"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center">
                <MailOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-emerald-300/70">No conversations yet</p>
                <p className="text-gray-500 text-sm mt-1">Send an email to get started</p>
              </div>
            ) : (
              conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => selectConversation(conv)}
                  className={`p-4 border-b border-emerald-800/30/50 cursor-pointer transition-all hover:bg-[#0a3d2e]/30 ${
                    selectedConversation?.id === conv.id ? 'bg-[#0a3d2e]/50 border-l-2 border-l-emerald-500' : ''
                  } ${!conv.is_read ? 'bg-[#0a3d2e]/20' : ''}`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      conv.lead?.temperature === 'hot' ? 'bg-red-500/20' :
                      conv.lead?.temperature === 'warm' ? 'bg-amber-500/20' :
                      'bg-slate-700'
                    }`}>
                      <User className={`w-5 h-5 ${
                        conv.lead?.temperature === 'hot' ? 'text-red-400' :
                        conv.lead?.temperature === 'warm' ? 'text-amber-400' :
                        'text-emerald-300/70'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium truncate ${!conv.is_read ? 'text-white' : 'text-slate-300'}`}>
                          {conv.lead_name}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {formatDate(conv.last_message_at)}
                        </span>
                      </div>
                      <p className={`text-sm truncate ${!conv.is_read ? 'text-slate-300' : 'text-gray-500'}`}>
                        {conv.subject}
                      </p>
                      <p className="text-xs text-slate-600 truncate mt-1">
                        {conv.snippet}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={`${getStatusColor(conv.status)} border text-xs py-0`}>
                          {conv.status}
                        </Badge>
                        {/* Channel indicators */}
                        {conv.channels?.includes('whatsapp') && (
                          <Badge className="bg-green-500/20 text-green-400 text-xs py-0">
                            <MessageCircle className="w-3 h-3 mr-1" />
                            WhatsApp
                          </Badge>
                        )}
                        {conv.lead?.status && (
                          <Badge className={`${getLeadStatusColor(conv.lead.status)} text-xs py-0`}>
                            {conv.lead.status}
                          </Badge>
                        )}
                        {!conv.is_read && (
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Conversation Detail */}
        <div className="flex-1 flex flex-col bg-[#052e22]/30">
          {!selectedConversation ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-emerald-300/70 text-lg">Select a conversation</p>
                <p className="text-gray-500 text-sm">Choose from your inbox to view messages</p>
              </div>
            </div>
          ) : (
            <>
              {/* Conversation Header */}
              <div className="flex-shrink-0 p-4 border-b border-emerald-800/30 bg-[#052e22]/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button 
                      onClick={() => setSelectedConversation(null)}
                      className="lg:hidden p-2 text-emerald-300/70 hover:text-white"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h2 className="text-lg font-semibold text-white">{selectedConversation.lead_name}</h2>
                      <p className="text-sm text-emerald-300/70">{selectedConversation.lead_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => markAsUnread(selectedConversation.id)}
                      className="text-emerald-300/70 hover:text-white"
                    >
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => updateStatus(selectedConversation.id, 'closed')}
                      className="text-emerald-300/70 hover:text-white"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteConversation(selectedConversation.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Lead Info */}
                {conversationLead && (
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    {conversationLead.company && (
                      <span className="flex items-center gap-1 text-emerald-300/70">
                        <Building className="w-4 h-4" />
                        {conversationLead.company}
                      </span>
                    )}
                    <Badge className={`${getLeadStatusColor(conversationLead.status)}`}>
                      {conversationLead.status}
                    </Badge>
                    {conversationLead.tags?.map(tag => (
                      <Badge key={tag} className="bg-slate-700 text-slate-300 text-xs">
                        {tag}
                      </Badge>
                    ))}
                    <span className="text-gray-500">
                      Score: <span className="text-white font-medium">{conversationLead.score}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loadingConversation ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-emerald-500"></div>
                  </div>
                ) : (
                  conversationMessages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-2xl rounded-xl p-4 ${
                        msg.direction === 'outbound'
                          ? 'bg-emerald-600/20 border border-emerald-500/30'
                          : 'bg-[#0a3d2e] border border-emerald-800/30'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {/* Channel indicator */}
                          {msg.channel === 'whatsapp' ? (
                            <MessageCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <Mail className="w-4 h-4 text-emerald-400" />
                          )}
                          <span className={`text-sm font-medium ${
                            msg.direction === 'outbound' ? 'text-emerald-300' : 'text-slate-300'
                          }`}>
                            {msg.direction === 'outbound' ? 'You' : selectedConversation.lead_name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatFullDate(msg.sent_at || msg.received_at || msg.created_at)}
                          </span>
                          {msg.channel === 'whatsapp' && (
                            <Badge className="bg-green-500/20 text-green-400 text-xs py-0">
                              WhatsApp
                            </Badge>
                          )}
                          {msg.source && msg.source !== 'manual' && msg.source !== 'whatsapp' && (
                            <Badge className="bg-slate-700/50 text-emerald-300/70 text-xs py-0">
                              via {msg.source}
                            </Badge>
                          )}
                        </div>
                        {msg.subject && (
                          <p className="text-white font-medium mb-2">{msg.subject}</p>
                        )}
                        <p className="text-slate-300 whitespace-pre-wrap">{msg.content}</p>
                        
                        {/* Email Tracking Status */}
                        {msg.direction === 'outbound' && msg.channel !== 'whatsapp' && (
                          <MessageTrackingStatus message={msg} events={conversationEvents.filter(e => e.message_id === msg.id)} />
                        )}
                        
                        {/* WhatsApp Status */}
                        {msg.channel === 'whatsapp' && msg.direction === 'outbound' && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                            {msg.whatsapp_status === 'sent' && <Check className="w-3 h-3" />}
                            {msg.whatsapp_status === 'delivered' && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                            {msg.whatsapp_status === 'read' && <CheckCircle2 className="w-3 h-3 text-blue-400" />}
                            <span>{msg.whatsapp_status || msg.status}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Composer */}
              <div className="flex-shrink-0 p-4 border-t border-emerald-800/30 bg-[#052e22]/50">
                <div className="bg-[#0a3d2e] rounded-xl border border-emerald-800/30 overflow-hidden">
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply..."
                    rows={4}
                    className="w-full p-4 bg-transparent text-white placeholder-slate-500 resize-none focus:outline-none"
                    data-testid="reply-input"
                  />
                  <div className="flex items-center justify-between p-3 border-t border-emerald-800/30/50">
                    <div className="flex items-center gap-2">
                      <button className="p-2 text-gray-500 hover:text-slate-300 rounded">
                        <Bold className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-500 hover:text-slate-300 rounded">
                        <Italic className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-500 hover:text-slate-300 rounded">
                        <Link className="w-4 h-4" />
                      </button>
                    </div>
                    <Button
                      onClick={sendReply}
                      disabled={!replyContent.trim() || sendingReply}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="send-reply-btn"
                    >
                      {sendingReply ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Reply
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      <AnimatePresence>
        {showCompose && (
          <ComposeModal
            token={token}
            onClose={() => setShowCompose(false)}
            onSent={() => {
              setShowCompose(false);
              fetchConversations();
              fetchStats();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Compose Modal Component
function ComposeModal({ token, onClose, onSent }) {
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [searchLead, setSearchLead] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const filteredLeads = leads.filter(lead =>
    lead.name?.toLowerCase().includes(searchLead.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchLead.toLowerCase())
  );

  const sendEmail = async () => {
    if (!selectedLead || !subject.trim() || !content.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/inbox/compose`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: selectedLead.id,
          subject,
          content
        })
      });

      if (res.ok) {
        toast.success('Email sent!');
        onSent();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#052e22] border border-emerald-800/30 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-emerald-800/30">
          <h3 className="text-lg font-semibold text-white">Compose Email</h3>
          <button onClick={onClose} className="text-emerald-300/70 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Lead Selector */}
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">To</label>
            {selectedLead ? (
              <div className="flex items-center justify-between p-3 bg-[#0a3d2e] rounded-lg">
                <div>
                  <p className="text-white font-medium">{selectedLead.name}</p>
                  <p className="text-sm text-emerald-300/70">{selectedLead.email}</p>
                </div>
                <button onClick={() => setSelectedLead(null)} className="text-emerald-300/70 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchLead}
                  onChange={(e) => setSearchLead(e.target.value)}
                  className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
                {searchLead && (
                  <div className="mt-2 max-h-40 overflow-y-auto bg-[#0a3d2e] border border-emerald-800/30 rounded-lg">
                    {filteredLeads.slice(0, 5).map(lead => (
                      <button
                        key={lead.id}
                        onClick={() => { setSelectedLead(lead); setSearchLead(''); }}
                        className="w-full p-3 text-left hover:bg-slate-700 transition-colors"
                      >
                        <p className="text-white">{lead.name}</p>
                        <p className="text-sm text-emerald-300/70">{lead.email}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              data-testid="compose-subject"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm text-emerald-300/70 mb-2">Message</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your message..."
              rows={8}
              className="w-full px-4 py-2 bg-[#0a3d2e] border border-emerald-800/30 rounded-lg text-white placeholder-slate-500 resize-none focus:outline-none focus:border-emerald-500"
              data-testid="compose-content"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-emerald-800/30">
          <Button variant="ghost" onClick={onClose} className="text-emerald-300/70">
            Cancel
          </Button>
          <Button
            onClick={sendEmail}
            disabled={!selectedLead || !subject.trim() || !content.trim() || sending}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="compose-send-btn"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Email
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Message Tracking Status Component
function MessageTrackingStatus({ message, events }) {
  const formatEventTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  // Group events by type
  const deliveredEvents = events.filter(e => e.event_type === 'email.delivered');
  const openedEvents = events.filter(e => e.event_type === 'email.opened');
  const clickedEvents = events.filter(e => e.event_type === 'email.clicked');
  const bouncedEvents = events.filter(e => e.event_type === 'email.bounced');

  const hasDelivered = deliveredEvents.length > 0 || message.delivered_at;
  const hasOpened = openedEvents.length > 0 || message.opened_at;
  const hasClicked = clickedEvents.length > 0 || message.clicked_at;
  const hasBounced = bouncedEvents.length > 0 || message.status === 'bounced';

  return (
    <div className="mt-3 pt-3 border-t border-emerald-800/30/50">
      {/* Status Pills */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {/* Sent */}
        <div className="flex items-center gap-1 text-xs text-emerald-300/70">
          <Check className="w-3 h-3" />
          <span>Sent</span>
        </div>

        {/* Delivered */}
        {hasDelivered && (
          <div className="flex items-center gap-1 text-xs text-emerald-400">
            <CheckCircle2 className="w-3 h-3" />
            <span>Delivered</span>
          </div>
        )}

        {/* Opened */}
        {hasOpened && (
          <div className="flex items-center gap-1 text-xs text-blue-400">
            <MailOpen className="w-3 h-3" />
            <span>
              Opened {message.open_count > 1 ? `(${message.open_count}x)` : ''}
            </span>
          </div>
        )}

        {/* Clicked */}
        {hasClicked && (
          <div className="flex items-center gap-1 text-xs text-emerald-400">
            <MousePointer className="w-3 h-3" />
            <span>
              Clicked {message.click_count > 1 ? `(${message.click_count}x)` : ''}
            </span>
          </div>
        )}

        {/* Bounced */}
        {hasBounced && (
          <div className="flex items-center gap-1 text-xs text-red-400">
            <XCircle className="w-3 h-3" />
            <span>Bounced</span>
          </div>
        )}
      </div>

      {/* Detailed Timeline (expandable) */}
      {events.length > 0 && (
        <details className="text-xs">
          <summary className="text-gray-500 cursor-pointer hover:text-emerald-300/70">
            View tracking timeline ({events.length} events)
          </summary>
          <div className="mt-2 space-y-1 pl-2 border-l border-emerald-800/30">
            {events.map((event, idx) => (
              <div key={idx} className="flex items-center gap-2 py-1">
                {event.event_type === 'email.delivered' && (
                  <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                )}
                {event.event_type === 'email.opened' && (
                  <MailOpen className="w-3 h-3 text-blue-400" />
                )}
                {event.event_type === 'email.clicked' && (
                  <MousePointer className="w-3 h-3 text-emerald-400" />
                )}
                {event.event_type === 'email.bounced' && (
                  <XCircle className="w-3 h-3 text-red-400" />
                )}
                <span className="text-emerald-300/70">
                  {event.event_type.replace('email.', '').charAt(0).toUpperCase() + 
                   event.event_type.replace('email.', '').slice(1)}
                </span>
                <span className="text-slate-600">
                  {formatEventTime(event.created_at)}
                </span>
                {event.link_url && (
                  <span className="text-gray-500 truncate max-w-[150px]" title={event.link_url}>
                    {event.link_url}
                  </span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
